import { useState } from "react";
import { SettingsCard } from "../../../../../layout/portalPageLayout";
import { H3, H6 } from "../../../../../components/Typography/Typography";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { useStoreState } from "../../../../../util/util";
import styled from "styled-components/macro";
import type { AxiosError } from "axios";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import {
  CancelButtonSmall,
  EditButton,
} from "../../../../../components/Buttons/Buttons";
import { Row } from "../../../../../components/Layout/Layout";
import { InfoBlockExtraSmall } from "../../../../../components/InfoBlocks/InfoBlocks";
import { ProductNotificationsForm } from "./ProductNotificationsForm";
import { REMINDER_SELECT_BOX_OPTIONS } from "./ReminderNotifications"; // Reuse options for display
import type { StorefrontProductEmailRemindersSchema } from "../../../../../types/types";
import { spacingTokens } from "../../../../../designTokens";

export const ReminderGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(min-content, 180px) minmax(75px, 200px);
  grid-row: auto auto;
  grid-column-gap: ${({ theme }) => theme.resolve("spacing_2xl")};
  grid-row-gap: ${({ theme }) => theme.resolve("spacing_2xl")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

export function ProductNotifications() {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const [editMode, setEditMode] = useState(false);

  const {
    data: productReminders,
    error: productRemindersError,
    mutate: mutateProductReminders,
  } = useSWR<StorefrontProductEmailRemindersSchema, AxiosError>(
    `/v2/storefronts/${storefront_id}/product-reminders`,
    {
      revalidateOnFocus: false,
    }
  );

  const isLoadingProductReminders = !productReminders && !productRemindersError;

  if (productRemindersError) {
    return (
      <SettingsCard>
        <ErrorPlaceholder
          message={t(
            "There was an error loading product reminder configuration"
          )}
        />
      </SettingsCard>
    );
  } else if (isLoadingProductReminders) {
    return (
      <SettingsCard>
        <DelayedSpinner />
      </SettingsCard>
    );
  } else if (productReminders) {
    return (
      <SettingsCard>
        <Row>
          <H3>{t("Product Reminders")}</H3>
          {!editMode && (
            <EditButton
              testid={"edit-product-reminders"}
              onClick={() => {
                setEditMode(true);
              }}
              title={t("Edit Product Reminders")}
            />
          )}
          {editMode && (
            <CancelButtonSmall
              onClick={() => {
                setEditMode(false);
              }}
            />
          )}
        </Row>
        <p style={{ marginTop: spacingTokens.spacing_none }}>
          {t(
            "Enable or disable reminders based on your preferences, and configure when they will be sent."
          )}
          <br />
          {t(
            "You can set reminders to trigger a specific number of days before an event."
          )}
        </p>
        {!editMode ? (
          // Display Mode
          <>
            {productReminders.reminder_data.map(
              ({ reminder_type, first_reminder, second_reminder }, idx) => {
                const firstReminderLabel =
                  REMINDER_SELECT_BOX_OPTIONS.find(
                    ({ value }) => value === first_reminder
                  )?.label ?? "--";
                const secondReminderLabel =
                  REMINDER_SELECT_BOX_OPTIONS.find(
                    ({ value }) => value === second_reminder
                  )?.label ?? "--";

                return (
                  <div key={`${reminder_type}_${idx}`}>
                    <H6 style={{ marginBottom: spacingTokens.spacing_2xl }}>
                      {t("Assets Expiring")}
                    </H6>
                    <ReminderGrid>
                      <InfoBlockExtraSmall
                        header={t("First Reminder sent before")}
                        content={firstReminderLabel}
                      />
                      <InfoBlockExtraSmall
                        header={t("Second Reminder sent before")}
                        content={secondReminderLabel}
                      />
                    </ReminderGrid>
                  </div>
                );
              }
            )}
          </>
        ) : (
          <ProductNotificationsForm
            productReminders={productReminders}
            onSuccess={() => {
              setEditMode(false);
              mutateProductReminders();
            }}
          />
        )}
      </SettingsCard>
    );
  } else return null;
}
